import { IS_DEBUG } from '@/core/constants/appConfig/environment';
import { TMableEvent } from '@/core/constants/event_types/base_mable_event';
import { EMableEventName } from '@/core/constants/event_types/events';
import { ScriptHandler } from '@/core/handlers/script';
import { EDestinationType } from '@/core/interfaces';
import { transformMableEventToGA4Event } from '@/destinations/bundles/google_analytics_4/event_transformer';
import { GA4Event } from '@/destinations/bundles/google_analytics_4/types';
import {
  DataLayerListener,
  EDataLayerType,
} from '@/destinations/shared/datalayer_listener/datalayer_listener';
import { processGoogleUserData } from '@/destinations/shared/gtag/tag_manager_wrapper/user_data_helper';
import { WSGTMWrapper } from '@/destinations/shared/gtag/tag_manager_wrapper/wsgtm_wrapper';
import { DestinationStatusHandler } from '@/destinations/status';
import { TWSGTMDestinationCredentials } from '@/destinations/types/destination_creds_types';
import { Logger } from '@/shared/logger/logger';

import {
  DestinationBundle,
  DestinationPayload,
  DestinationSendingResult,
} from '../abstract';

export class WSGTMDestinationBundle extends DestinationBundle {
  override allowedEvents: EMableEventName[] = [
    EMableEventName.PAGE_VIEWED,
    EMableEventName.PRODUCT_ADDED,
    EMableEventName.CHECKOUT_STARTED,
    EMableEventName.PAYMENT_INFO_ADDED,
    EMableEventName.PRODUCT_VIEWED,
    EMableEventName.LEAD,
    EMableEventName.CUSTOM_EVENT,
    EMableEventName.ORDER_COMPLETED,
  ];

  override type: EDestinationType = EDestinationType.WSGTM;
  private readonly wsgtm: WSGTMWrapper;
  private readonly containerId: string;
  private readonly serverUrl: string;
  private readonly gtmDomainStatus!: any;
  override hasBrowserDestination = true;

  constructor(
    destinationSetting: TWSGTMDestinationCredentials,
    destinationId: string,
  ) {
    super(EDestinationType.WSGTM, destinationSetting, destinationId);

    if (!destinationSetting?.container_id) {
      throw new Error(
        'WSGTMDestinationBundle: Missing required configuration parameter: containerId',
      );
    }

    if (!destinationSetting?.sgtm_container_url) {
      throw new Error(
        'WSGTMDestinationBundle: Missing required configuration parameter: serverUrl',
      );
    }

    this.containerId = destinationSetting.container_id;
    this.serverUrl = destinationSetting.sgtm_container_url;
    this.wsgtm = new WSGTMWrapper();

    // Initialize status handler for GTM
    this.gtmDomainStatus = new DestinationStatusHandler({
      destinationUrl: `${this.serverUrl}/healthz}`,
      storageNamespace: 'wsgtmDomain(Status)',
    });
  }

  override getBrowserDestinationSendingStatus(): boolean {
    return !!this.gtmDomainStatus.isAvailable;
  }

  override async init(): Promise<void> {
    try {
      // this.gtmDomainStatus = await this.gtmDomainStatus.getStatus();
      DataLayerListener.getInstance().initializeLayer(EDataLayerType.WSGTM);
      this.wsgtm.initWSGTM(this.containerId, this.serverUrl);

      Logger.debug('WSGTMDestinationBundle: Initialized', {
        containerId: this.containerId,
        serverUrl: this.serverUrl,
      });
    } catch (error) {
      Logger.error(
        'WSGTMDestinationBundle: Error initializing destination',
        error,
      );
    }
  }

  override async calculatePayload(
    mableEvent: TMableEvent,
  ): Promise<DestinationPayload> {
    const payload = transformMableEventToGA4Event(mableEvent);

    if (IS_DEBUG) {
      Logger.debug('WSGTMDestinationBundle: Calculated payload', {
        eventName: mableEvent.en,
        ga4EventName: payload.event_name,
      });
    }

    const googleUserData = await processGoogleUserData(mableEvent.cdas);

    return {
      event_name: payload.event_name,
      event_params: payload.event_params,
      user_data: googleUserData,
    };
  }

  override async sendToDestination(
    payload: GA4Event,
  ): Promise<DestinationSendingResult> {
    try {
      if (payload.event_params.user_id) {
        this.wsgtm.setUserId(payload.event_params.user_id);
      }

      payload.event_params['source_uid'] =
        ScriptHandler.getInstance().getSourceUid();

      this.wsgtm.pushToDataLayer(payload.event_name, payload.event_params);

      return {
        destinationReqEvent: payload,
        reason: 'Event successfully sent to WSGTM',
        success: true,
      };
    } catch (error) {
      return {
        destinationReqEvent: payload,
        reason:
          error instanceof Error ? error.message : 'Unknown error occurred',
        success: false,
      };
    }
  }
}
